/**
 * Hooks de lectura del dominio de Horario (TanStack Query).
 *
 * Datos de servidor -> TanStack Query (`docs/frontend_conventions.md`, Datos): no
 * `useState`+`useEffect`+`fetch`. La red la gobierna `lib/api` (cookie httpOnly +
 * `X-Sede-Id`). La rejilla se cachea por sede + modo (láser) + semana; el detalle de
 * un slot, por sede + modo + fecha/hora. Al cambiar de sede/semana/slot se pide la
 * combinación correspondiente.
 *
 * `useSchedule` acepta `enabled`: la pantalla solo activa la query con el permiso
 * correspondiente (`schedule` / `laser_schedule`), de modo que **no se pide** el
 * horario que el actor no debe ver. `useSlotOrders` solo se habilita cuando hay un
 * slot abierto (fecha + hora), para no pedir el detalle hasta que el usuario abre una
 * celda.
 */
import { useQuery } from "@tanstack/react-query";

import { getActiveSedeId } from "@/lib/sede";

import { fetchSchedule, fetchSlotOrders } from "../api";
import type { Schedule, SlotOrders } from "../types";
import { scheduleQueryKey, slotQueryKey } from "./queryKeys";

/** Margen corto compartido: evita refetch agresivo sin volver los datos rancios. */
const STALE_TIME = 30_000;

/**
 * Rejilla semanal de la sede activa para el desplazamiento `weekOffset`. `laser`
 * elige el horario normal o el láser. Solo se pide con `enabled` (gating del permiso
 * de la pantalla).
 */
export function useSchedule(
  weekOffset: number,
  laser: boolean,
  enabled = true,
) {
  const sedeId = getActiveSedeId();
  return useQuery<Schedule>({
    queryKey: scheduleQueryKey(sedeId, laser, weekOffset),
    queryFn: () => fetchSchedule(weekOffset, laser),
    enabled,
    staleTime: STALE_TIME,
  });
}

/**
 * Órdenes de un slot (fecha + hora) de la sede activa. `laser` elige el endpoint
 * normal o láser. Solo se habilita cuando hay un slot seleccionado (fecha y hora no
 * nulas): así el detalle no se pide hasta abrir una celda.
 */
export function useSlotOrders(
  date: string | null,
  time: string | null,
  laser: boolean,
) {
  const sedeId = getActiveSedeId();
  return useQuery<SlotOrders>({
    queryKey: slotQueryKey(sedeId, laser, date, time),
    queryFn: () => fetchSlotOrders(date as string, time as string, laser),
    enabled: date !== null && time !== null,
    staleTime: STALE_TIME,
  });
}
